// Painel para conectar Google/Meta (usado no onboarding — compact — e reaproveitável em outras telas).
import { useLocation } from 'react-router-dom'
import { CheckCircle2, Lock } from 'lucide-react'
import type { AdProvider } from '@impulso/shared'
import type { ConnectionDto } from '@impulso/shared/api/connections'
import { errorMessage } from '../../lib/api'
import { ErrorState, Loading, PlatformLogo } from '../../ui'
import { useConnections, useProviders, useStartConnection } from './api'
import { brand, statusBadge } from './format'
import OAuthReturn from './OAuthReturn'

export default function ConnectAccountsPanel({ companyId, compact }: { companyId: string; compact?: boolean }) {
  const location = useLocation()
  const providers = useProviders()
  const conns = useConnections(companyId)
  const start = useStartConnection(companyId)

  if (providers.isLoading || conns.isLoading) return <Loading lines={3} />
  if (providers.error || conns.error) return <ErrorState error={providers.error ?? conns.error} onRetry={() => { void providers.refetch(); void conns.refetch() }} />

  const items = providers.data?.items ?? []
  const byProvider = (p: AdProvider) => (conns.data?.items ?? []).filter((c: ConnectionDto) => c.provider === p)
  const connect = (p: AdProvider) => start.mutate({ provider: p, returnTo: location.pathname })

  return (
    <div className="stack" style={{ gap: compact ? 10 : 14 }}>
      <OAuthReturn companyId={companyId} />
      {items.length === 0 && <div className="card empty"><p>Nenhuma plataforma de anúncios está disponível agora.</p></div>}
      {items.map((p) => {
        const mine = byProvider(p.provider)
        const connected = mine.length > 0
        return (
          <div key={p.provider} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: compact ? 14 : undefined }}>
            <PlatformLogo platform={brand(p.provider)} size="lg" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="strong">{p.name}</div>
              {connected ? (
                mine.map((c) => {
                  const b = statusBadge(c)
                  return (
                    <div key={c.id} className="small mt-8 row wrap" style={{ gap: 6 }}>
                      <span className={`badge ${b.cls}`}>{b.cls === 'green' && <CheckCircle2 size={12} />} {b.text}</span>
                      <span className="muted" style={{ overflowWrap: 'anywhere' }}>{c.externalAccountName ?? c.externalAccountId}</span>
                    </div>
                  )
                })
              ) : (
                <div className="small muted">{p.description}</div>
              )}
            </div>
            {(!connected || !compact) && (
              <button className={`btn sm ${connected ? '' : 'primary'}`} disabled={start.isPending} onClick={() => connect(p.provider)}>
                {connected ? 'Conectar outra' : 'Conectar'}
              </button>
            )}
          </div>
        )
      })}
      {start.error && <div className="form-error" role="alert">{errorMessage(start.error)}</div>}
      <div className="row small muted" style={{ gap: 8, alignItems: 'flex-start' }}>
        <Lock size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>Nunca pedimos sua senha. A autorização é feita na página oficial do Google e da Meta, e você pode desconectar quando quiser.</span>
      </div>
    </div>
  )
}
